import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import { AmountAndConversion, FixedBottomButton } from '@/components'
import type { TransactionSuccessParams } from '@/types/sendRequest'
import { getCoinGif } from '@/constants/coinGifs'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import useOffsets from '@/hooks/useOffsets'
import { useAppAnalytics } from '@/services/analytics'

export const TransactionSuccessScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const offsets = useOffsets()
  const displayCurrency = useAppPreferencesStore(
    (state) => state.displayCurrency,
  )

  // Get success data from location state
  const successData = location.state as TransactionSuccessParams | undefined
  const { trackEvent, events } = useAppAnalytics()

  useEffect(() => {
    if (successData) {
      const isRequest = successData.title?.toLowerCase().includes('request')
      trackEvent(events.MONEY_ACTION_COMPLETED, {
        action_type: isRequest ? 'request' : 'send',
        amount: parseFloat(successData.amount),
        currency: successData.code,
        // to_user_type: 'kem_user', // Optional, hard to derive without more data
      })
    }
  }, [successData, trackEvent, events.MONEY_ACTION_COMPLETED])

  if (!successData) {
    return (
      <BaseView
        className="flex-1 justify-center items-center p-6"
        style={{ backgroundColor: '#1F2937' }}
      >
        <BaseText
          className="text-center text-lg font-medium"
          style={{ color: '#EF4444' }} // red-500
        >
          {'No transaction data found'}
        </BaseText>
        <FixedBottomButton
          buttons={{
            label: 'Go Home',
            onPress: () => navigate('/dashboard'),
            type: 'primaryGradient',
          }}
        />
      </BaseView>
    )
  }

  const handleDone = () => {
    // If it was a request, navigate to activity page (similar to React Native)
    // If it was a send, navigate to dashboard
    if (successData.title?.toLowerCase().includes('request')) {
      navigate('/activity', { replace: true })
    } else {
      navigate('/dashboard', { replace: true })
    }
  }

  return (
    <BaseView className="flex-1" style={{ backgroundColor: '#141417' }}>
      {/* Main Content Container - Full height with flex */}
      <BaseView
        className="flex-1 px-3"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          paddingTop: offsets.top - 30,
        }}
      >
        {/* Centered content that takes available space */}
        <BaseView
          className="items-center flex-1"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: '48px',
            marginTop: '60px',
          }}
        >
          {/* Currency Icon - exactly 180px like React Native */}
          <img
            src={getCoinGif(successData.code)}
            alt={successData.code}
            className="object-contain"
            style={{
              width: '180px',
              height: '180px',
            }}
          />

          {/* Content Section with 24px gap like React Native */}
          <BaseView className="items-center" style={{ gap: '24px' }}>
            {/* Title - 24px font size like React Native */}
            <BaseText
              className="font-bold text-center"
              style={{
                fontSize: '24px',
                color: '#FFFFFF',
              }}
            >
              {successData.title}
            </BaseText>

            {/* Amount Display - using AmountAndConversion component */}
            <AmountAndConversion
              cryptoAmount={{
                amount: successData.amount,
                code: successData.code,
              }}
              fiatAmount={{
                amount:
                  successData.relativeFiatFormatted?.replace(/[^\d.,]/g, '') ||
                  '0',
                code: displayCurrency?.isin || 'USD',
              }}
              showDecimals={true}
              shouldMaskStablecoinAsFiat={false}
            />

            {successData.description && (
              <BaseText
                fontFamily="medium"
                className="text-center"
                color="label.description"
                style={{
                  fontSize: '24px',
                }}
              >
                {successData.description}
              </BaseText>
            )}
          </BaseView>
        </BaseView>
      </BaseView>

      {/* Bottom Button - Fixed at bottom */}
      <FixedBottomButton
        buttons={{
          label: t('form:actions.done'),
          onPress: handleDone,
          type: 'secondaryGray',
        }}
      />
    </BaseView>
  )
}

export default TransactionSuccessScreen
